import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import { usePWA } from '../hooks/usePWA'

const { FiDownload, FiX, FiCheck, FiSmartphone, FiWifi, FiBell } = FiIcons

const PWAInstallPrompt = () => {
  const { isInstallable, isInstalled, installApp, requestNotificationPermission } = usePWA()
  const [showPrompt, setShowPrompt] = useState(true)
  const [installing, setInstalling] = useState(false)

  // Don't show if already installed or not installable
  if (isInstalled || !isInstallable || !showPrompt) {
    return null
  }

  const handleInstall = async () => {
    setInstalling(true)
    try {
      const success = await installApp()
      if (success) {
        // Request notification permission after install
        await requestNotificationPermission()
        setShowPrompt(false)
      }
    } catch (error) {
      console.error('Failed to install app:', error)
    } finally {
      setInstalling(false)
    }
  }

  const handleDismiss = () => {
    setShowPrompt(false)
    // Remember user dismissed the prompt
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString())
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50"
      >
        <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiDownload} className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Install Promptly</h3>
                <p className="text-sm text-slate-600">Get the full app experience</p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="p-1 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiX} className="w-4 h-4 text-slate-400" />
            </button>
          </div>

          <div className="space-y-3 mb-6">
            <div className="flex items-center space-x-3 text-sm text-slate-600">
              <SafeIcon icon={FiSmartphone} className="w-4 h-4 text-green-500" />
              <span>Works offline - create prompts anywhere</span>
            </div>
            <div className="flex items-center space-x-3 text-sm text-slate-600">
              <SafeIcon icon={FiWifi} className="w-4 h-4 text-blue-500" />
              <span>Faster loading with app shell caching</span>
            </div>
            <div className="flex items-center space-x-3 text-sm text-slate-600">
              <SafeIcon icon={FiBell} className="w-4 h-4 text-purple-500" />
              <span>Get notified about new features</span>
            </div>
          </div>

          <div className="flex space-x-3">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleInstall}
              disabled={installing}
              className="flex-1 bg-gradient-to-r from-red-600 to-orange-600 text-white py-3 px-4 rounded-lg font-medium hover:from-red-700 hover:to-orange-700 transition-all flex items-center justify-center space-x-2 disabled:opacity-50"
            >
              <SafeIcon icon={installing ? FiCheck : FiDownload} className="w-4 h-4" />
              <span>{installing ? 'Installing...' : 'Install App'}</span>
            </motion.button>
            <button
              onClick={handleDismiss}
              className="px-4 py-3 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              Later
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  )
}

export default PWAInstallPrompt